import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MdArrowBack, MdKeyboardArrowDown, MdKeyboardArrowRight } from 'react-icons/md';
import API from '../../../services/api';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { LogoMark, fmtDate } from '../ResearchPage';
import EvidenceStrip from './EvidenceStrip';
import PrimerReader from './PrimerReader';
import ScoresPanel from './ScoresPanel';
import ChatterPanel from './ChatterPanel';

// The company profile — the read side of the search-first master surface. ONE screen that
// assembles everything ResearchOS holds for a single name: the evidence summary (scores,
// chatter and the feeds still to come), the primer, this name's chatter, and the full
// scorecard. It fetches /api/research-feed/company/<ticker>/, whose response already reconciles
// scores + notebook + chatter slice server-side, so this component only lays them out.
//
// EACH PANEL SELF-HIDES when its data is absent. A name with a score but no primer shows no
// primer panel — not an empty one — because absence here is a real state (a name can be scored
// before its notebook is written), and an empty document rendered as if it were one is a lie the
// reader cannot tell from a broken page. The one thing always shown is the evidence strip, which
// states pending feeds AS pending rather than hiding them.

// A collapsible panel. The section title always shows; the body toggles. Chevron on the left,
// matching the trading tree — one disclosure idiom across the portal.
function Section({ title, subtitle, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  // NO `overflow-hidden` here: it would become a containing block for `position: sticky` and
  // silently disable the primer's sticky chapter nav nested inside. The rounded corners are kept
  // by rounding the header (and it is the only child with a background), so nothing needs
  // clipping — the body is transparent on the page.
  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800">
      <button
        type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-t-xl px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/40"
      >
        <span className="grid w-5 shrink-0 place-items-center text-gray-400">
          {open ? <MdKeyboardArrowDown size={18} /> : <MdKeyboardArrowRight size={18} />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="font-serif text-lg text-gray-900 dark:text-gray-100">{title}</span>
          {subtitle
            ? <span className="ml-2 text-[11px] text-gray-400">{subtitle}</span> : null}
        </span>
      </button>
      {open && <div className="border-t border-gray-100 p-4 dark:border-gray-800/60">{children}</div>}
    </section>
  );
}

export default function CompanyProfile() {
  const { ticker } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(''); setData(null);
    API.get(`/api/research-feed/company/${encodeURIComponent(ticker)}/`)
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch((e) => {
        if (!alive) return;
        // 404 is a real answer ("no research for this name"), distinct from a transport failure.
        setErr(e.response?.status === 404 ? 'notfound' : 'error');
        setLoading(false);
      });
    return () => { alive = false; };
  }, [ticker]);

  const Crumb = () => (
    <div className="mb-4 flex items-center gap-2 text-[11px]">
      <Link to="/research"
            className="flex shrink-0 items-center gap-1 text-gray-400 transition-colors hover:text-brand-gold dark:text-gray-500">
        <MdArrowBack size={14} />
        ResearchOS
      </Link>
    </div>
  );

  if (loading) {
    return <div><Crumb /><LoadingSpinner message={`Loading ${(ticker || '').toUpperCase()}…`} /></div>;
  }

  if (err) {
    const notfound = err === 'notfound';
    return (
      <div>
        <Crumb />
        <div className="rounded-xl border border-gray-200 p-8 text-center dark:border-gray-800">
          <h1 className="font-serif text-xl text-gray-900 dark:text-gray-100">
            {(ticker || '').toUpperCase()}
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {notfound
              ? 'No research is held for this name yet.'
              : 'That could not be loaded. Please try again.'}
          </p>
          <Link to="/research"
                className="mt-4 inline-block text-[13px] text-brand-gold hover:underline">
            Back to ResearchOS
          </Link>
        </div>
      </div>
    );
  }

  const scores = Array.isArray(data.scores) ? data.scores : [];
  const primary = scores[0] || null;                    // latest team; the headline scorecard
  const hasPrimer = !!data.notebook;
  const hasChatter = !!data.chatter;

  return (
    // Horizontal overflow is guarded globally at the root (`html { overflow-x: hidden }` in
    // index.css) — the robust, all-Safari fix for the "zoomed-out on load" symptom that keeps
    // the primer's sticky nav working. No per-page clip needed here.
    <div>
      <Crumb />

      {/* Header — the name, its classification, and the date the research is as-of. The scored
          date (known_at) is shown, not the push send time: it is when the assessment was made. */}
      <div className="mb-5 flex items-start gap-3">
        <LogoMark ticker={data.ticker} size={44} />
        <div className="min-w-0 flex-1">
          <h1 className="flex flex-wrap items-baseline gap-x-2.5 font-serif text-[26px] leading-tight text-gray-900 sm:text-3xl dark:text-gray-100">
            {data.ticker}
            {data.security_name
              ? <span className="text-base font-normal text-gray-400">{data.security_name}</span>
              : null}
          </h1>
          <div className="mt-1 flex flex-wrap gap-x-2 text-[11px] text-gray-400">
            {data.sector ? <span>{data.sector}</span> : null}
            {data.industry ? <span>· {data.industry}</span> : null}
            {primary?.known_at ? <span>· scored {fmtDate(primary.known_at)}</span> : null}
            {primary?.method_version ? <span>· method {primary.method_version}</span> : null}
          </div>
        </div>
      </div>

      {/* The neutral evidence summary. Always shown — it is what states the pending feeds. */}
      <div className="mb-4">
        <EvidenceStrip score={primary} chatter={data.chatter}
                       signals={data.signals} technical={data.technical} />
      </div>

      {/* Every panel starts COLLAPSED — the reader opens what they want. The profile opens as a
          compact index (header + evidence strip + panel titles) that fits one screen, rather than
          a wall of primer prose. This also keeps the first paint free of the primer's tall body,
          so the tiles above render at true 1:1 scale on a phone. */}
      <div className="flex flex-col gap-3">
        {hasPrimer && (
          <Section title="Primer" defaultOpen={false}
                   subtitle={data.notebook_as_at ? `frozen ${fmtDate(data.notebook_as_at)}` : 'research notebook'}>
            <PrimerReader notebook={data.notebook} />
          </Section>
        )}

        {hasChatter && (
          <Section title="Chatter" subtitle="from public conversation" defaultOpen={false}>
            <ChatterPanel chatter={data.chatter} />
          </Section>
        )}

        {primary && (
          <Section title="Scorecard" subtitle="agent proposes · analyst decides"
                   defaultOpen={false}>
            <ScoresPanel score={primary} others={scores.slice(1)} />
          </Section>
        )}

        {!hasPrimer && !hasChatter && !primary && (
          <p className="text-sm text-gray-400">Nothing to show for this name yet.</p>
        )}
      </div>
    </div>
  );
}
